import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSubjectList } from 'modules/subject';
import type { Subject } from 'modules/subject/types';
import {
  useBankQuestionSingle,
  useCreateBankQuestion,
  useUpdateBankQuestion,
} from 'modules/questionBank';
import type { CreateBankQuestionData } from 'modules/questionBank/api';
import { QuestionEditorTwoPane } from './QuestionEditorTwoPane';

/** Debounced autosave (no explicit Save); after create it switches to update so the draft saves in place. */
export const BankQuestionEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { questionId } = useParams<{ questionId?: string }>();
  const [searchParams] = useSearchParams();
  const defaultSubjectId = searchParams.get('subjectId') ?? undefined;

  const { data: subjects = [] } = useSubjectList();
  const { data: question, isLoading } = useBankQuestionSingle(questionId);

  const createQuestion = useCreateBankQuestion();
  const updateQuestion = useUpdateBankQuestion();

  // Autosave: the first successful create yields an id we then update against,
  // so the two-pane can keep saving the same draft without navigating away.
  const savedIdRef = React.useRef<string | undefined>(questionId);

  const handleSubmit = async (data: CreateBankQuestionData) => {
    const id = savedIdRef.current;
    if (id) {
      await updateQuestion.mutateAsync({ id, data });
    } else {
      const created = await createQuestion.mutateAsync(data);
      if (created?.id) savedIdRef.current = created.id;
    }
  };

  if (questionId && (isLoading || !question)) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <QuestionEditorTwoPane
      key={questionId ? (question?.id || questionId) : 'new'}
      subjects={subjects as Subject[]}
      initialData={questionId ? (question ?? undefined) : undefined}
      defaultSubjectId={defaultSubjectId}
      onSubmit={handleSubmit}
      onDone={() => navigate('/app/questions')}
    />
  );
};

export default BankQuestionEditPage;
